import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Pagination from '../../../../components/ui/Pagination';
import routes from '../../../../config/routes';
import { convertFirebaseTSToDate } from '../../../../utils/helpers';
import QuestionCard from './question-item/QuestionContainer';
import type { Category, Question } from '../../../../data/interface-question';
import type { QuestionBankID } from '../../../../data/interface-question-bank';

interface QuestionListProps {
	categories: Category[];
	questions: Question[] | undefined;
	filteredQuestions?: Question[] | undefined;
	questionBankID: QuestionBankID;
}

const QuestionList = ({
	categories,
	questions,
	filteredQuestions,
	questionBankID
}: QuestionListProps) => {
	type SortBy = 'create-desc' | 'create-asc' | 'modified-desc' | 'modified-asc';
	// DATA STATE
	const [sortBy, setSortBy] = useState<SortBy>('create-desc');
	const [paginationSize, setPaginationSize] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);

	// Use a single useMemo to handle both sorting and pagination
	const { currentTableData, totalCount } = useMemo(() => {
		// Use filteredQuestions if available, otherwise fall back to questions
		const questionList = filteredQuestions ?? questions ?? [];

		// SORTING
		const sortedData = questionList?.sort((a: Question, b: Question) => {
			const aCreateDate = new Date(convertFirebaseTSToDate(a.createdOn));
			const bCreateDate = new Date(convertFirebaseTSToDate(b.createdOn));
			const aEditDate = new Date(convertFirebaseTSToDate(a.lastEditDate));
			const bEditDate = new Date(convertFirebaseTSToDate(b.lastEditDate));

			if (sortBy === 'create-asc' || sortBy === 'create-desc') {
				if (b.createdOn === undefined || a.createdOn === undefined) {
					return 1;
				} else if (sortBy === 'create-desc') {
					return bCreateDate > aCreateDate ? 1 : -1;
				} else if (sortBy === 'create-asc') {
					return aCreateDate > bCreateDate ? 1 : -1;
				} else {
					return 0;
				}
			} else {
				// Handle modified sorting
				if (a.lastEditDate === undefined && b.lastEditDate === undefined) {
					return 0; // Both are not modified, maintain original order
				} else if (a.lastEditDate === undefined) {
					return 1; // a is not modified, put it last
				} else if (b.lastEditDate === undefined) {
					return -1; // b is not modified, put it last
				} else if (sortBy === 'modified-desc') {
					return bEditDate > aEditDate ? 1 : -1;
				} else if (sortBy === 'modified-asc') {
					return aEditDate > bEditDate ? 1 : -1;
				} else {
					return 0;
				}
			}
		});

		// PAGINATION
		const firstPageIndex = (currentPage - 1) * paginationSize;
		const lastPageIndex = firstPageIndex + paginationSize;
		const paginatedData = sortedData.slice(firstPageIndex, lastPageIndex);

		return {
			currentTableData: paginatedData,
			totalCount: sortedData.length
		};
	}, [currentPage, questions, filteredQuestions, paginationSize, sortBy]);

	return (
		<div>
			{/* PAGINATION */}
			<div className='flex justify-end mb-2'>
				<Pagination
					className='pagination-bar'
					currentPage={currentPage}
					siblingCount={1}
					totalCount={totalCount}
					pageSize={paginationSize}
					onPageChange={(page) => setCurrentPage(page)}
				/>
				<select
					className='appearance-none rounded-md shadow-sm text-gray-600 border-gray-300 focus:border-brand-200 focus:ring focus:ring-brand-200 focus:ring-opacity-50 ml-2 text-xs'
					value={sortBy}
					onChange={(e) => setSortBy(e.target.value as SortBy)}>
					<option value='create-desc'>Sort by createTS desc</option>
					<option value='create-asc'>Sort by createTS asc</option>
					<option value='modified-desc'>Sort by modifiedTS desc</option>
					<option value='modified-asc'>Sort by modifiedTS asc</option>
				</select>
				<select
					className='appearance-none rounded-md shadow-sm text-gray-600 border-gray-300 focus:border-brand-200 focus:ring focus:ring-brand-200 focus:ring-opacity-50 ml-2 text-xs'
					value={paginationSize}
					onChange={(e) => setPaginationSize(Number(e.target.value))}>
					<option value={10}>10</option>
					<option value={20}>20</option>
					<option value={30}>30</option>
					<option value={40}>40</option>
					<option value={50}>50</option>
					<option value={100}>100</option>
				</select>
			</div>

			{/* DISPLAY DATA */}
			{currentTableData.map((question) => {
				const route = routes.ADMIN_LINKS.QUESTIONS_EDIT;
				const path = `${route}/${question.id}`;
				return (
					<Link to={path} key={question.id}>
						<QuestionCard
							categories={categories ?? []}
							question={question}
							questionBankID={questionBankID}
							sortBy={sortBy}
						/>
					</Link>
				);
			})}
		</div>
	);
};
export default QuestionList;
